/**
 * Reconnaître une erreur de décalage de versions, et s'en remettre.
 *
 * Après un déploiement, un navigateur qui a gardé d'anciens morceaux de
 * JavaScript en cache les rejoue contre le nouveau runtime. Turbopack résout
 * ses modules par identifiants NUMÉRIQUES (`e.i(10568)`), et ces identifiants
 * changent d'un build à l'autre : l'ancien morceau croit charger la
 * configuration cartographique et reçoit un autre module. D'où, observé en
 * production :
 *
 *     Uncaught TypeError: Cannot read properties of undefined (reading 'west')
 *     Uncaught Error: Minified React error #130   (composant = undefined)
 *
 * Sans frontière d'erreur, Next affiche son écran « This page couldn't load »,
 * et « Reload » recharge la même page depuis le même cache : impasse.
 *
 * Le remède est un rechargement DUR, qui redemande le document et repart sur
 * les morceaux du build courant. `deploymentId` (cf. next.config.ts) évite le
 * problème en amont, mais il faut que la plateforme fournisse `SOURCE_COMMIT` ;
 * ceci fonctionne dans tous les cas.
 */

/**
 * L'erreur ressemble-t-elle à un morceau de code périmé ?
 *
 * On reste volontairement étroit : recharger sur une erreur applicative
 * ordinaire ferait perdre son écran au client sans rien réparer.
 */
export function isVersionSkewError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");

  return (
    // Chargement d'un morceau qui n'existe plus sur le serveur.
    /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message,
    ) ||
    // Module résolu vers le mauvais voisin : les symptômes observés en prod.
    /Cannot read propert(?:y|ies) of undefined \(reading '(?:west|south|east|north|center)'\)/i.test(
      message,
    ) ||
    // React #130 : « element type is invalid », c'est-à-dire un composant
    // importé qui vaut `undefined` — signature d'un module mal résolu.
    /Minified React error #130/i.test(message)
  );
}

/**
 * Horodatage du dernier rechargement automatique, et délai de garde.
 *
 * Un simple drapeau « déjà rechargé » ne suffisait pas : il fallait le lever au
 * démarrage suivant pour que le déploiement d'après puisse guérir à son tour —
 * ce qui rouvrait la boucle, puisque l'erreur revenue effaçait aussitôt le
 * garde-fou. Mesuré : deux rechargements de suite sur la même panne.
 *
 * Un délai tranche les deux cas sans les confondre. Une vraie panne rejoue
 * l'erreur en quelques secondes et se heurte au délai : un seul rechargement,
 * puis l'écran d'erreur. Un déploiement suivant survient bien plus tard et
 * retrouve son droit au rechargement.
 */
const RELOAD_AT_KEY = "fast_skew_reload_at";
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * Recharge une fois, en dur. Renvoie `true` si le rechargement est lancé, pour
 * que l'appelant puisse afficher une attente plutôt qu'un message d'erreur.
 */
export function recoverFromVersionSkew(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Stockage indisponible (navigation privée stricte) : sans mémoire du
    // dernier essai, on renonce plutôt que de risquer une boucle sans fin.
    return false;
  }
  window.location.reload();
  return true;
}
